import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideCheck, LucideDownload } from '@lucide/angular';

export type TimelineStatus = 'done' | 'current' | 'upcoming';

export interface TimelineAttachment {
  filename: string;
}

export interface TimelineItem {
  title: string;
  description?: string;
  time?: string;
  status: TimelineStatus;
  imageUrl?: string;
  imageLabel?: string;
  actor?: string;
  attachments?: TimelineAttachment[];
}

/** Chronological progress of a request or case, oldest first. */
@Component({
  selector: 'ds-timeline',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <ol class="line">
      @for (item of items(); track $index) {
        <li class="event" [class]="'event event--' + item.status">
          <span class="event__dot" aria-hidden="true">
            @if (item.status === 'done') {
              <ng-container
                [ngComponentOutlet]="checkIcon"
                [ngComponentOutletInputs]="{ size: 16, strokeWidth: 3.5 }"
              />
            }
          </span>
          <div class="event__body">
            <span class="event__title">
              {{ item.title }}
              @if (item.status === 'done') {
                <span class="sr-only">(selesai)</span>
              } @else if (item.status === 'current') {
                <span class="event__now">Saat ini</span>
              }
            </span>
            @if (item.description) {
              <span class="event__desc">{{ item.description }}</span>
            }
            @if (item.actor) {
              <span class="event__actor">oleh {{ item.actor }}</span>
            }
            @if (item.attachments && item.attachments.length > 0) {
              <ul class="event__files">
                @for (att of item.attachments; track att.filename) {
                  <li class="file-row">
                    <span class="file-name">{{ att.filename }}</span>
                    <button
                      type="button"
                      class="file-download"
                      [attr.aria-label]="'Unduh ' + att.filename"
                      (click)="download.emit(att.filename)"
                    >
                      <ng-container
                        [ngComponentOutlet]="downloadIcon"
                        [ngComponentOutletInputs]="{ size: 16, strokeWidth: 2 }"
                      />
                      <span>Unduh</span>
                    </button>
                  </li>
                }
              </ul>
            }
            @if (item.time) {
              <span class="event__time">{{ item.time }}</span>
            }
            @if (item.imageUrl) {
              <a
                class="event__link"
                [href]="item.imageUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ item.imageLabel || 'Lihat Gambar' }}
              </a>
            }
          </div>
        </li>
      }
    </ol>
  `,
  styles: `
    .line {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .event {
      position: relative;
      display: flex;
      gap: var(--space-3);
      padding-bottom: var(--space-5);

      &:not(:last-child)::before {
        content: '';
        position: absolute;
        left: 13px;
        top: 32px;
        bottom: 2px;
        width: 3px;
        border-radius: 2px;
        background: var(--color-border);
      }

      &--done:not(:last-child)::before {
        background: var(--color-teal-300);
      }

      &:last-child {
        padding-bottom: 0;
      }
    }

    .event__dot {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      margin-top: 2px;
      border-radius: 50%;
      border: 2.5px solid var(--color-border-input);
      background: var(--color-surface);
      color: var(--color-text-on-primary);
    }

    .event--done .event__dot {
      background: var(--color-teal-600);
      border-color: var(--color-teal-600);
    }

    .event--current .event__dot {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 4px var(--color-primary-tint);
      animation: pulse-dot 1.5s ease-in-out infinite;
    }

    @keyframes pulse-dot {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.7;
        transform: scale(1.1);
      }
    }

    .event__body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding-top: var(--space-1);
    }

    .event__title {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-weight: var(--font-weight-bold);
      color: var(--color-text);
    }

    .event--upcoming .event__title {
      color: var(--color-text-secondary);
      font-weight: var(--font-weight-regular);
    }

    .event__now {
      padding: 1px var(--space-2);
      border-radius: var(--radius-full);
      background: var(--color-primary-tint);
      color: var(--color-teal-800);
      font-size: var(--text-sm);
      white-space: nowrap;
    }

    .event__desc {
      color: var(--color-text-secondary);
      font-size: var(--text-sm);
    }

    .event__actor {
      color: var(--color-text-secondary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
    }

    .event__time {
      color: var(--color-text-secondary);
      font-size: var(--text-sm);
    }

    .event__files {
      list-style: none;
      margin: var(--space-1) 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
    }

    .file-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-2);
      padding: var(--space-1) var(--space-2);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-sm);
      background: var(--color-surface);
    }

    .file-name {
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      overflow-wrap: anywhere;
    }

    .file-download {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      flex-shrink: 0;
      padding: var(--space-1) var(--space-2);
      border: 0;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-primary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      cursor: pointer;

      &:hover { color: var(--color-primary-hover); background: var(--color-primary-tint); }
      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }
    }

    .event__link {
      align-self: flex-start;
      color: var(--color-primary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium);
      text-decoration: underline;

      &:hover {
        color: var(--color-primary-hover);
      }

      &:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
        border-radius: var(--radius-sm);
      }
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }
  `,
})
export class TimelineComponent {
  items = input.required<TimelineItem[]>();
  download = output<string>();

  protected readonly checkIcon = LucideCheck;
  protected readonly downloadIcon = LucideDownload;
}
